// src/pages/Apply.js
// ──────────────────────────────────────────────────────────────────────────
// MTI application form (our own — replaces the old external link). Submits via
// the existing ContactAPI; if the backend isn't ready it falls back to opening
// a pre-filled email so applications still reach mbalainstitute@gmail.com.
// ──────────────────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ContactAPI from '../api/contact';
import { supabase, supabaseReady } from '../lib/supabase';
import { SAMPLE_PROGRAMS, FACULTIES } from '../api/programs';
import theme from '../theme/theme';

const FUNDING = ['Self-sponsored', 'CDF (Constituency Development Fund)', 'Donor / NGO sponsorship', 'Employer / organisation sponsorship'];

const Header = styled.header`
  background: ${theme.color.blueDeep}; color: #fff;
  padding: 7rem ${theme.layout.gutter} 5rem;
  .inner { max-width: ${theme.layout.maxWidth}; margin: 0 auto; }
  h1 { font-weight: 400; font-size: clamp(2.4rem, 6vw, 4rem); color: #fff; margin-top: 1.2rem; }
  p { margin-top: 1rem; max-width: 52ch; font-weight: 300; color: rgba(255,255,255,0.82); }
  .kicker { color: #fff; }
`;

const Wrap = styled.section`
  max-width: 820px; margin: 0 auto; padding: 4rem ${theme.layout.gutter} 6rem;
`;

const Form = styled.div`
  .legend { font-family: ${theme.font.body}; font-size: 0.72rem; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase; color: ${theme.color.accent}; margin: 2rem 0 0.5rem; }
  label { display: block; font-size: 0.8rem; font-weight: 600; color: ${theme.color.inkSoft}; margin: 0.9rem 0 0.4rem; }
  input, select, textarea {
    width: 100%; font-family: ${theme.font.body}; font-size: 0.98rem; padding: 11px 14px;
    border: 1px solid ${theme.color.line}; border-radius: ${theme.layout.radius}; background: #fff; color: ${theme.color.ink};
    &:focus { outline: none; border-color: ${theme.color.accent}; }
  }
  textarea { min-height: 120px; resize: vertical; }
  .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; @media (max-width: 560px) { grid-template-columns: 1fr; } }
  button {
    margin-top: 2rem; background: ${theme.color.accent}; color: ${theme.color.onAccent};
    border: none; font-family: ${theme.font.body}; font-size: 0.92rem; font-weight: 700;
    padding: 14px 32px; border-radius: ${theme.layout.radius}; cursor: pointer;
    &:hover { background: ${theme.color.accentDark}; } &:disabled { opacity: 0.6; cursor: default; }
  }
  .note { margin-top: 1.1rem; font-size: 0.92rem; } .ok { color: #1a7f4b; } .err { color: #b3261e; }
`;

const Done = styled.div`
  text-align: center; padding: 3rem 0;
  .tick { width: 64px; height: 64px; border-radius: 50%; background: ${theme.color.lemonSoft}; color: ${theme.color.accent}; display: grid; place-items: center; font-size: 1.8rem; margin: 0 auto 1.4rem; }
  h2 { font-weight: 400; font-size: 2rem; } p { color: ${theme.color.inkSoft}; margin: 0.8rem 0 1.6rem; }
  a { color: ${theme.color.accent}; font-weight: 700; }
`;

const empty = { name: '', dob: '', gender: '', phone: '', email: '', town: '', dept: '', course: '', education: '', funding: '', notes: '' };

function Apply() {
  const contact = theme.info;
  const [f, setF] = useState(empty);
  const [status, setStatus] = useState(null); // sending | ok | err
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const courses = f.dept ? SAMPLE_PROGRAMS.filter((p) => p.faculty === f.dept) : SAMPLE_PROGRAMS;

  const brief =
    `APPLICATION\n` +
    `Name: ${f.name}\nDate of birth: ${f.dob}\nGender: ${f.gender}\n` +
    `Phone: ${f.phone}\nEmail: ${f.email}\nTown/area: ${f.town}\n` +
    `Department: ${f.dept}\nCourse: ${f.course}\n` +
    `Education: ${f.education}\nFunding: ${f.funding}\n\nNotes: ${f.notes}`;

  const mailto = `mailto:${contact?.email || 'mbalainstitute@gmail.com'}?subject=${encodeURIComponent('Application — ' + (f.name || ''))}&body=${encodeURIComponent(brief)}`;

  const submit = async () => {
    setStatus('sending');
    try {
      if (supabaseReady) {
        // Write the application into the secure "applications" table.
        const { error } = await supabase.from('applications').insert({
          name: f.name, dob: f.dob || null, gender: f.gender || null,
          phone: f.phone, email: f.email || null, town: f.town || null,
          department: f.dept || null, course: f.course,
          education: f.education || null, funding: f.funding || null,
          notes: f.notes || null,
        });
        if (error) throw error;
      } else {
        // Backend not configured yet — fall back to the old contact endpoint.
        await ContactAPI.submit({ name: f.name, email: f.email, phone: f.phone, projectType: f.course, budget: f.funding, brief });
      }
      setStatus('ok');
    } catch {
      setStatus('err');
    }
  };

  return (
    <>
      <Navbar />
      <Header>
        <div className="inner">
          <span className="kicker">Admissions</span>
          <h1>Apply to MTI</h1>
          <p>Fill in the form below to apply for a skills course. Intakes run throughout the year and everyone is welcome to start where they are.</p>
        </div>
      </Header>

      <Wrap>
        {status === 'ok' ? (
          <Done>
            <div className="tick" aria-hidden="true">✓</div>
            <h2>Application received</h2>
            <p>Thank you, {f.name || 'applicant'}. We’ll be in touch about the next steps and your intake.</p>
            <Link to="/programs">Browse more courses →</Link>
          </Done>
        ) : (
          <Form>
            <div className="legend">Your details</div>
            <div className="grid2">
              <div><label>Full name *</label><input value={f.name} onChange={set('name')} /></div>
              <div><label>Phone *</label><input value={f.phone} onChange={set('phone')} /></div>
            </div>
            <div className="grid2">
              <div><label>Email</label><input type="email" value={f.email} onChange={set('email')} /></div>
              <div><label>Town / area</label><input value={f.town} onChange={set('town')} /></div>
            </div>
            <div className="grid2">
              <div><label>Date of birth</label><input type="date" value={f.dob} onChange={set('dob')} /></div>
              <div>
                <label>Gender</label>
                <select value={f.gender} onChange={set('gender')}>
                  <option value="">Prefer not to say</option><option>Female</option><option>Male</option>
                </select>
              </div>
            </div>

            <div className="legend">Course</div>
            <div className="grid2">
              <div>
                <label>Department</label>
                <select value={f.dept} onChange={(e) => setF((p) => ({ ...p, dept: e.target.value, course: '' }))}>
                  <option value="">Any department</option>
                  {FACULTIES.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label>Course of interest *</label>
                <select value={f.course} onChange={set('course')}>
                  <option value="">Select a course</option>
                  {courses.map((c) => <option key={c.id} value={c.title}>{c.title}</option>)}
                </select>
              </div>
            </div>

            <div className="legend">Background</div>
            <label>Highest education / grade completed</label>
            <input value={f.education} onChange={set('education')} placeholder="e.g. Grade 9, Grade 12, certificate…" />
            <label>How will your training be funded?</label>
            <select value={f.funding} onChange={set('funding')}>
              <option value="">Select an option</option>
              {FUNDING.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
            <label>Anything else you’d like us to know?</label>
            <textarea value={f.notes} onChange={set('notes')} />

            <button onClick={submit} disabled={status === 'sending' || !f.name || !f.phone || !f.course}>
              {status === 'sending' ? 'Submitting…' : 'Submit application'}
            </button>

            {status === 'err' && (
              <p className="note err">
                We couldn’t submit automatically. Please send your application by email instead:{' '}
                <a href={mailto}>{contact?.email || 'mbalainstitute@gmail.com'}</a>.
              </p>
            )}
            <p className="note" style={{ color: theme.color.inkSoft, fontSize: '0.82rem' }}>* Required. Need help? <Link to="/contact" style={{ color: theme.color.accent }}>Contact us</Link>.</p>
          </Form>
        )}
      </Wrap>

      <Footer />
    </>
  );
}

export default Apply;
